import { Store } from 'vuex';
import { container } from '../../container';
import { DEPENDENCIES } from '../../container/dependencies.const';
import { CurrencyOptions } from '../../i18n';
import { logger } from '../../logger';
import { GETTERS_KEYS, RootStoreState } from '../../store';
import { CurrencyLocation, DecimalSeparator, RoundingMethod } from './currency-formatter.const';

export class CurrencyFormatter {
  private options: CurrencyOptions;

  constructor(private readonly customOptions?: CurrencyOptions) {
    this.options = customOptions;
  }

  public format(value: number | string): string {
    if (!this.customOptions) {
      const store = container.get<Store<RootStoreState>>(DEPENDENCIES.Store);
      this.options = store.getters[GETTERS_KEYS.config.currency];
    }
    const numberValue = this.convertToNumberIfNecessary(value);
    const formattedValue = this.formatValue(numberValue);
    return this.addCurrencySymbolIfNeeded(formattedValue);
  }

  private convertToNumberIfNecessary(value: number | string): number {
    if (typeof value === 'number') {
      return value;
    } else {
      return Number.parseFloat(value);
    }
  }

  private formatValue(value: number) {
    let formattedValue = this.formatDecimals(value);
    formattedValue = this.formatDecimalSeparator(formattedValue);
    formattedValue = this.addThousandsSeparatorIfNeeded(formattedValue);
    return formattedValue;
  }

  private formatDecimals(value: number): string {
    if (this.shouldDisplayDecimals(value)) {
      return this.formatWithFixedDecimals(value);
    } else {
      return this.formatWithoutDecimals(value);
    }
  }

  private shouldDisplayDecimals(value: number): boolean {
    return !Number.isInteger(value) || !this.options.hideDecimalsIfZero;
  }

  private formatWithoutDecimals(value: number): string {
    if (this.options.roundingMethod === RoundingMethod.ROUND) {
      return value.toFixed();
    } else if (this.options.roundingMethod === RoundingMethod.TRUNCATE) {
      return Math.trunc(value).toString();
    } else {
      return value.toString();
    }
  }

  private formatWithFixedDecimals(value: number): string {
    const { roundingMethod, decimalDigits } = this.options;
    if (roundingMethod === RoundingMethod.ROUND) {
      return value.toFixed(decimalDigits);
    } else if (roundingMethod === RoundingMethod.TRUNCATE) {
      return this.truncate(value, decimalDigits).toFixed(decimalDigits);
    } else {
      return value.toString();
    }
  }

  private truncate(value: number, digits: number): number {
    const fixed = Math.pow(10, digits);
    return (Math.floor(value * fixed) / fixed);
  }

  private formatDecimalSeparator(formattedValue: string): string {
    const { decimalSeparator, decimalDigits } = this.options;
    if (decimalDigits && decimalSeparator === DecimalSeparator.COMMA) {
      return formattedValue.replace('.', ',');
    } else {
      return formattedValue;
    }
  }

  private addThousandsSeparatorIfNeeded(formattedValue: string): string {
    if (this.needsThousandsSeparator(formattedValue)) {
      return this.addThousandsSeparator(formattedValue);
    } else {
      return formattedValue;
    }
  }

  private needsThousandsSeparator(formattedValue: string): boolean {
    const { thousandsSeparator, decimalSeparator } = this.options;
    const wholePart = formattedValue.split(decimalSeparator)[0];
    return thousandsSeparator && wholePart.length > 3;
  }

  private addThousandsSeparator(formattedValue: string): string {
    const { decimalSeparator, thousandsSeparator } = this.options;
    const [wholePart, decimalPart] = formattedValue.split(decimalSeparator);
    const formattedWholePart = this.getThousandsGroups(wholePart).join(thousandsSeparator);
    return decimalPart ? `${ formattedWholePart }${ decimalSeparator }${ decimalPart }` : formattedWholePart;
  }

  private getThousandsGroups(wholePart: string): string[] {
    if (wholePart.length > 3) {
      return [...this.getThousandsGroups(wholePart.slice(0, -3)), wholePart.slice(-3)];
    } else {
      return [wholePart];
    }
  }

  private addCurrencySymbolIfNeeded(formattedValue: string): string {
    if (this.options.currencySymbol) {
      return this.addCurrencySymbol(formattedValue);
    } else {
      return formattedValue;
    }
  }

  private addCurrencySymbol(formattedValue: string): string {
    const { currencySymbol, currencyLocation, currencySpacing } = this.options;
    const currencySpacingCharacter = currencySpacing ? ' ' : '';
    if (currencyLocation === CurrencyLocation.PREPEND) {
      return `${ currencySymbol }${ currencySpacingCharacter }${ formattedValue }`;
    } else if (currencyLocation === CurrencyLocation.APPEND) {
      return `${ formattedValue }${ currencySpacingCharacter }${ currencySymbol }`;
    } else {
      logger.warn('[Currency-formatter]', `Invalid currencyLocation parameter: ${ currencyLocation }`);
      return formattedValue;
    }
  }
}
